import { useQuery } from '@apollo/client';
import { IonText } from '@ionic/react';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import DateAndTimeStamp from '@components/dates/DateAndTimeStamp';
import HandleQuery from '@components/HandleQuery';
import Table, { TableHeader } from '@components/Table';
import InternalLink from '@components/typography/InternalLink';
import useSubscribeToAllMessages from '@hooks/subscriptions/useSubscribeToAllMessages';
import useMyAdmin from '@hooks/useMyAdmin';
import useMyExpert from '@hooks/useMyExpert';
import useNodesFromConnection from '@hooks/useNodesFromConnection';
import { GetMessagesDocument, GetMessagesQuery, GetMessagesQueryVariables, SortDirectionEnum } from '@typing/Generated';
import { getCareProfileName, messagePresentedName } from '@utils/modelUtils';
import { journeyRoute } from '@utils/routeUtils';
import { truncate } from '@utils/stringUtils';

type Message = NonNullable<NonNullable<NonNullable<GetMessagesQuery['messages']['edges']>[number]>['node']>;

const MessagesTable = () => {
  const getMessagesQuery = useQuery<GetMessagesQuery, GetMessagesQueryVariables>(GetMessagesDocument);
  const myAdmin = useMyAdmin();
  const myExpert = useMyExpert();
  const intl = useIntl();

  useSubscribeToAllMessages(() => {
    getMessagesQuery.refetch();
  });

  const messages = useNodesFromConnection(getMessagesQuery.data?.messages);

  const tableHeaders: TableHeader<SortDirectionEnum>[] = [
    { messageKey: 'models.messages.tableHeadings.sentAt' },
    { messageKey: 'models.messages.tableHeadings.from' },
    { messageKey: 'models.messages.tableHeadings.journey' },
    { messageKey: 'models.messages.tableHeadings.preview' },
    { messageKey: 'models.messages.tableHeadings.attachments' }
  ];

  const generateMessageLink = useCallback(
    (message: Message) => {
      if (myAdmin) {
        return `/admin/messages/${message.id}`;
      }
      return `/expert/messages/${message.id}`;
    },
    [myAdmin]
  );

  const generateJourneyLink = useCallback(
    (message: Message) => {
      if (myAdmin) {
        return `/admin/clients/${message.chat.journey.owner.id}`;
      }
      return journeyRoute({ journeyId: message.chat.journey.id, linkAfterJourney: '', myExpert });
    },
    [myAdmin, myExpert]
  );

  return (
    <HandleQuery query={getMessagesQuery}>
      {data => (
        <Table<SortDirectionEnum>
          fetchMore={getMessagesQuery.fetchMore}
          headerKey="models.messages.tableHeadings"
          headers={tableHeaders}
          pageInfo={data.messages.pageInfo}
        >
          {messages.map(message => (
            <tr key={message.id}>
              <td>
                <InternalLink to={generateMessageLink(message)}>
                  <DateAndTimeStamp showWeekday={false} value={message.createdAt} />
                </InternalLink>
              </td>
              <td>
                <IonText>{messagePresentedName(message, intl)}</IonText>
              </td>
              <td>
                <InternalLink to={generateJourneyLink(message)}>
                  <FormattedMessage
                    id="models.journey.nameWithOwner"
                    values={{
                      lovedOne: getCareProfileName({ intl, journey: message.chat.journey }),
                      owner: message.chat.journey.name
                    }}
                  />
                </InternalLink>
              </td>
              <td>{truncate(message.bodyPlainText, 200)}</td>
              <td>{message.fileResources.length}</td>
            </tr>
          ))}
        </Table>
      )}
    </HandleQuery>
  );
};

export default MessagesTable;
